import { useState, useEffect } from 'react';
import { getS3DownloadUrl } from '@/services/data/s3Files';

/**
 * Returns whether a string looks like an already-resolved URL
 * (http, https, blob, data) vs an S3 key that needs signing.
 */
function isAbsoluteUrl(value: string): boolean {
  return /^(https?:|blob:|data:)/i.test(value);
}

/**
 * Resolves an S3 key to a presigned URL for streaming.
 * If the value is already an absolute URL or empty, returns it as-is.
 */
export function useSignedUrl(key: string | null | undefined): string | null {
  const [url, setUrl] = useState<string | null>(() => {
    if (!key) return null;
    if (isAbsoluteUrl(key)) return key;
    return null;
  });

  useEffect(() => {
    if (!key) {
      setUrl(null);
      return;
    }
    if (isAbsoluteUrl(key)) {
      setUrl(key);
      return;
    }

    let cancelled = false;
    getS3DownloadUrl(key, false).then((result) => {
      if (cancelled) return;
      if (result.success && result.data?.url) {
        setUrl(result.data.url);
      } else {
        console.warn('[useSignedUrl] Failed to resolve key:', key);
        setUrl(null);
      }
    });

    return () => { cancelled = true; };
  }, [key]);

  return url;
}
